// src/services/implementations/AdministratorsServiceImpl.ts
import { AdministratorsService } from '@/services/abstract/AdministratorsService';
import type { AdminEntity, AdminUpsertDTO, AdminId, AdminInfoResponse } from '@/types/administrators.types';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { administratorEndpoints } from '@/services/endpoints/administrator';
import type { AdminStatus } from '@/types/organization.types';

export class AdministratorsServiceImpl extends AdministratorsService {
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  // Create an admin (POST /private/v1/admins)
  async create(data: AdminUpsertDTO): Promise<AdminEntity> {
    return await this.fetcher.post<AdminEntity>(administratorEndpoints.createAdmin, data);
  }

  // List admins (GET /private/v1/admins)
  async list(): Promise<AdminEntity[]> {
    // If your abstract service interface expects a different name, adapt accordingly.
    return await this.fetcher.get<AdminEntity[]>(administratorEndpoints.listAdmins);
  }

  // Update admin (PATCH /private/v1/admins/:id)
  async update(id: AdminId, data: AdminUpsertDTO): Promise<AdminEntity> {
    return await this.fetcher.patch<AdminEntity>(administratorEndpoints.updateAdminById(id), data);
  }

  // Toggle active/inactive for an admin (POST /private/v1/admins/:id/update-status)
  async toggleActive(id: string, status: AdminStatus): Promise<void> {
    await this.fetcher.patch(administratorEndpoints.updateAdminStatusById(id), { status });
  }

  // Force logout admin (POST /private/v1/admins/:id/force-logout)
  async forceLogout(id: string): Promise<void> {
    await this.fetcher.post(administratorEndpoints.forceAdminLogoutById(id), {});
  }

  // Force credential reset for admin (POST /private/v1/admins/:id/force-login-credentials-reset)
  async forceLoginReset(id: string): Promise<void> {
    await this.fetcher.post(administratorEndpoints.resetAdminCredentialsById(id), {});
  }

  // Delete admin (DELETE /private/v1/admins/:id)
  async delete(id: string): Promise<void> {
    await this.fetcher.del(administratorEndpoints.deleteAdminById(id));
  }

  // Get admin info (GET /private/v1/admins/:id)
  async adminInfo(id: string): Promise<AdminInfoResponse> {
    return await this.fetcher.get<AdminInfoResponse>(administratorEndpoints.getAdminById(id));
  }
}
